import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listDids,
  createDid,
  updateDid,
  releaseDid,
  clearDidBlocks,
  getDidReputation,
  listQuarantinePending,
  purgeQuarantinedDid,
  type DidListParams,
  type Did,
} from "../api/dids";
import { listEligibleCampaigns } from "../api/queues";

const PAGE_SIZE = 50;

function NewDidModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const save = useMutation({
    mutationFn: () => createDid({ name, phone }).then(() => undefined),
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-3">
        <h2 className="text-lg font-bold">Add phone number</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Number</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="15551234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Label</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!phone || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Add"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function ReputationModal({ did, onClose }: { did: Did; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["did-reputation", did.did_id],
    queryFn: () => getDidReputation(did.did_id),
  });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-full overflow-auto space-y-4">
        <h2 className="text-lg font-bold">
          Reputation — <span className="font-mono">{did.phone}</span>
        </h2>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : !data ? (
          <p className="text-gray-500 text-sm">No data.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs uppercase text-gray-400">Hourly cap</div>
                <div className="text-lg font-bold">{data.hourly_cap}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs uppercase text-gray-400">Daily cap</div>
                <div className="text-lg font-bold">{data.daily_cap}</div>
              </div>
            </div>
            {data.warmup_active && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
                Warmup ramp active — caps are reduced for the first 7 days
                {data.age_days != null ? ` (age ${data.age_days}d)` : ""}.
              </p>
            )}

            <div>
              <h3 className="text-sm font-semibold mb-1">7-day call quality</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="py-1 text-gray-500">Attempts</td><td className="py-1 text-right">{data.attempts_7d.toLocaleString()}</td></tr>
                  <tr><td className="py-1 text-gray-500">Connected</td><td className="py-1 text-right">{data.connected_7d.toLocaleString()}</td></tr>
                  <tr><td className="py-1 text-gray-500">Connect rate</td><td className="py-1 text-right">{pct(data.connect_rate)}</td></tr>
                  <tr><td className="py-1 text-gray-500">SIP 603 (carrier decline)</td><td className={`py-1 text-right ${data.sip603_rate >= 0.25 ? "text-red-600 font-semibold" : ""}`}>{pct(data.sip603_rate)}</td></tr>
                  <tr><td className="py-1 text-gray-500">Fast hangup (≤2s)</td><td className={`py-1 text-right ${data.fast_hangup_rate >= 0.6 ? "text-red-600 font-semibold" : ""}`}>{pct(data.fast_hangup_rate)}</td></tr>
                  <tr><td className="py-1 text-gray-500">Recipients in cooldown</td><td className="py-1 text-right">{data.recipient_cooldown_count.toLocaleString()}</td></tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-1">Active carrier blocks</h3>
              {data.carrier_blocks.length === 0 ? (
                <p className="text-sm text-green-700">None — number is clear.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500">
                    <tr><th className="py-1">Carrier</th><th className="py-1">Reason</th><th className="py-1 text-right">Hits</th><th className="py-1 text-right">Until</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.carrier_blocks.map((b, i) => (
                      <tr key={i}>
                        <td className="py-1">{b.dest_carrier}</td>
                        <td className="py-1">{b.reason}</td>
                        <td className="py-1 text-right">{b.block_count}</td>
                        <td className="py-1 text-right text-gray-500">
                          {b.blocked_until ? new Date(b.blocked_until).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        <div className="pt-2">
          <button onClick={onClose} className="w-full bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function RoutingModal({
  did,
  onClose,
  onSaved,
}: {
  did: Did;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<"agents" | "external">(
    did.action_type === "agents" ? "agents" : "external",
  );
  const [campaignId, setCampaignId] = useState<string>(
    did.action_type === "agents" ? did.action_data ?? "" : "",
  );
  const [number, setNumber] = useState<string>(
    did.action_type === "external" ? did.action_data ?? "" : "",
  );
  const [error, setError] = useState<string | null>(null);

  const { data: campaigns = [] } = useQuery({
    queryKey: ["eligible-campaigns"],
    queryFn: listEligibleCampaigns,
  });

  const save = useMutation({
    mutationFn: () =>
      mode === "agents"
        ? updateDid(did.did_id, { action_type: "agents", action_data: campaignId })
        : updateDid(did.did_id, { action_type: "external", action_data: number }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message || "Save failed"),
  });

  const canSave = mode === "agents" ? campaignId !== "" : number.trim() !== "";

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">
          Inbound routing — <span className="font-mono">{did.phone}</span>
        </h2>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="routing-mode"
            checked={mode === "agents"}
            onChange={() => setMode("agents")}
            aria-label="Ring my agents"
          />
          Ring my agents
        </label>
        {mode === "agents" && (
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">Select a campaign…</option>
            {campaigns.map((c) => (
              <option key={c.campaign_id} value={String(c.campaign_id)}>
                {c.name} ({c.agent_count} agent{c.agent_count === 1 ? "" : "s"})
              </option>
            ))}
          </select>
        )}
        {mode === "agents" && campaigns.length === 0 && (
          <p className="text-xs text-gray-500">
            No campaigns have agents assigned yet. Assign agents to a campaign first.
          </p>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="routing-mode"
            checked={mode === "external"}
            onChange={() => setMode("external")}
            aria-label="Forward to a number"
          />
          Forward to a number
        </label>
        {mode === "external" && (
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="(412) 536-4219"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 pt-2">
          <button
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function DidsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [repDid, setRepDid] = useState<Did | null>(null);
  const [routeDid, setRouteDid] = useState<Did | null>(null);
  const [params, setParams] = useState<DidListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    include_retired: false,
  });

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["dids", params],
    queryFn: () => listDids(params),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["dids"] });
  const quarantine = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) => updateDid(id, { is_quarantined: on }),
    onSuccess: invalidate,
  });
  const release = useMutation({
    mutationFn: (id: number) => releaseDid(id),
    onSuccess: invalidate,
  });
  const clearBlocks = useMutation({
    mutationFn: (id: number) => clearDidBlocks(id),
    onSuccess: invalidate,
  });

  // Quarantine purge: release each quarantined number from the carrier one at a
  // time, then soft-delete locally. Sequential so progress is visible and one
  // carrier error never stalls the batch. stopRef lets the operator abort.
  const [purge, setPurge] = useState<
    { total: number; done: number; failed: number; current: string; running: boolean } | null
  >(null);
  const stopRef = useRef(false);

  async function runPurge() {
    let pending;
    try {
      pending = await listQuarantinePending();
    } catch (e) {
      alert(`Could not load quarantined numbers: ${(e as Error).message}`);
      return;
    }
    if (pending.count === 0) {
      alert("No quarantined numbers to purge.");
      return;
    }
    if (
      !confirm(
        `Release ${pending.count.toLocaleString()} quarantined number(s) from the carrier and ` +
          `delete them locally? This is irreversible.`,
      )
    )
      return;
    stopRef.current = false;
    setPurge({ total: pending.count, done: 0, failed: 0, current: "", running: true });
    let done = 0;
    let failed = 0;
    for (const d of pending.dids) {
      if (stopRef.current) break;
      setPurge((s) => (s ? { ...s, current: d.phone } : s));
      try {
        const r = await purgeQuarantinedDid(d.did_id);
        if (r.status === "deleted") done += 1;
        else failed += 1;
      } catch {
        failed += 1;
      }
      setPurge((s) => (s ? { ...s, done, failed } : s));
    }
    setPurge((s) => (s ? { ...s, running: false, current: "" } : s));
    invalidate();
  }

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Phone Numbers (DIDs)</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={params.include_retired ?? false}
              onChange={(e) =>
                setParams((p) => ({ ...p, include_retired: e.target.checked, page: 1 }))
              }
            />
            Show retired
          </label>
          <button
            onClick={runPurge}
            disabled={purge?.running}
            className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            title="Release every quarantined number from the carrier, then delete it locally"
          >
            {purge?.running ? "Purging…" : "Purge quarantined"}
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            Add number
          </button>
        </div>
      </div>

      {purge && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          <div className="flex items-center justify-between">
            <span>
              Purging quarantined numbers: {(purge.done + purge.failed).toLocaleString()} /{" "}
              {purge.total.toLocaleString()} released
              {purge.failed > 0 && (
                <span className="text-red-600"> · {purge.failed.toLocaleString()} failed</span>
              )}
              {purge.running && purge.current && (
                <span className="text-gray-500 font-mono"> · {purge.current}</span>
              )}
            </span>
            {purge.running ? (
              <button
                onClick={() => {
                  stopRef.current = true;
                }}
                className="text-red-700 hover:underline"
              >
                Stop
              </button>
            ) : (
              <span className="text-green-700">
                Done{purge.failed > 0 ? ` — ${purge.failed.toLocaleString()} failed (see logs)` : ""}
              </span>
            )}
          </div>
          <div className="mt-2 h-2 bg-amber-100 rounded overflow-hidden">
            <div
              className="h-full bg-red-500 transition-all"
              style={{ width: pct((purge.done + purge.failed) / Math.max(purge.total, 1)) }}
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} numbers`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Number</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Caller ID group</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-gray-500">
                    No DIDs.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((d: Did) => (
                  <tr key={d.did_id}>
                    <td className="px-4 py-2 font-mono">{d.phone}</td>
                    <td className="px-4 py-2">{d.name || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{d.did_group_name || "—"}</td>
                    <td className="px-4 py-2">
                      {d.is_retired ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">
                          retired
                        </span>
                      ) : d.is_blocked ? (
                        <span
                          className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
                          title={d.blocked_until ? `until ${new Date(d.blocked_until).toLocaleString()}` : ""}
                        >
                          blocked{d.block_reason ? `: ${d.block_reason}` : ""}
                        </span>
                      ) : d.is_quarantined ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                          quarantined
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                      <button onClick={() => setRepDid(d)} className="text-brand hover:underline">
                        Reputation
                      </button>
                      {!d.is_retired && (
                        <>
                          {(d.is_blocked || d.is_quarantined) && (
                            <button
                              onClick={() => clearBlocks.mutate(d.did_id)}
                              className="text-brand hover:underline"
                            >
                              Clear blocks
                            </button>
                          )}
                          <button
                            onClick={() => quarantine.mutate({ id: d.did_id, on: !d.is_quarantined })}
                            className="text-brand hover:underline"
                          >
                            {d.is_quarantined ? "Unquarantine" : "Quarantine"}
                          </button>
                          <button
                            onClick={() => setRouteDid(d)}
                            className="text-brand hover:underline"
                          >
                            Routing
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Release (retire) ${d.phone}?`)) release.mutate(d.did_id);
                            }}
                            className="text-red-600 hover:underline"
                          >
                            Release
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <NewDidModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            qc.invalidateQueries({ queryKey: ["dids"] });
          }}
        />
      )}
      {repDid && <ReputationModal did={repDid} onClose={() => setRepDid(null)} />}
      {routeDid && (
        <RoutingModal
          did={routeDid}
          onClose={() => setRouteDid(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["dids"] })}
        />
      )}
    </div>
  );
}
